import type {
  OAuthErrorEvent,
  OAuthResponseEvent,
  PickerCanceledEvent,
  PickerPickedEvent,
} from '@googleworkspace/drive-picker-element';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as Metrics from 'metrics/Metrics';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {
  DRIVE_SCOPE,
  GDriveAuthError,
  GDriveConsentDeniedError,
  GDriveMissingScopesError,
  GDrivePopupClosedError,
  clearLastAuthorizedEmail,
  fetchAndStoreAuthorizedEmail,
  getAccessToken,
  getLastAuthorizedEmail,
  getScopesMissingFromTokenResponse,
} from 'notebook/notes/attachments/googleDriveAuth';
import * as Logging from 'util/Logging';

import {
  GooglePickerAPIScriptNotLoadedError,
  validateAndDownloadGoogleDriveFiles,
} from '../attachments/GoogleDriveDownload/ValidateAndDownloadGoogleDriveFiles';

// Used to fetch the user's Google account email after they grant OAuth consent.
const USERINFO_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';

function showGetAccessTokenErrorToast(err: unknown): boolean {
  if (err instanceof GDrivePopupClosedError) {
    return true;
  }
  if (err instanceof GDriveConsentDeniedError) {
    ToastActions.pushToast({
      type: ToastType.WARNING,
      message: 'You denied access to Google Drive. Please try again and allow access to upload files.',
    });
    return true;
  }
  if (err instanceof GDriveMissingScopesError) {
    ToastActions.pushToast({
      type: ToastType.WARNING,
      message:
        'Google Drive file access was not fully granted. Please try again and ensure all requested permissions are accepted.',
    });
    return true;
  }
  if (err instanceof GDriveAuthError) {
    ToastActions.pushToast({
      type: ToastType.DANGER,
      message: 'Unable to connect to Google Drive. Please try again.',
    });
    return true;
  }
  return false;
}

function getDownloadToastMessage(numberOfFilesDownloaded: number, totalNumberOfFiles: number): string {
  const fileLabel = totalNumberOfFiles === 1 ? 'file' : 'files';
  return `Downloaded ${numberOfFilesDownloaded} out of ${totalNumberOfFiles} ${fileLabel} from Google Drive...`;
}

export interface CreateUploadDropdownGDrivePickerHandlersDeps {
  setIsPickerVisible: (visible: boolean) => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  fileId: string;
}

/**
 * Callbacks passed to the Google Drive Picker web component. Extracted for unit testing
 * (the lazy picker is not mounted when `isNodeTesting` is true).
 */
export function createUploadDropdownGDrivePickerHandlers(
  deps: CreateUploadDropdownGDrivePickerHandlersDeps
): {
  handleCanceled: (e: PickerCanceledEvent) => void;
  handleOauthResponse: (e: OAuthResponseEvent) => Promise<void>;
  handleOauthError: (e: OAuthErrorEvent) => void;
  handlePicked: (e: PickerPickedEvent) => Promise<void>;
  resetOauthFailureFlag: () => void;
} {
  const {setIsPickerVisible, onGoogleDriveFilesReady, fileId} = deps;

  // The Drive Picker web component can dispatch oauth-response/oauth-error
  // multiple times for a single user interaction (two internal code paths).
  // Gate the warn + metric so we count one failure per picker session.
  // Reset when a new picker session starts via resetOauthFailureFlag().
  let hasLoggedOauthFailure = false;

  const handleCanceled = (_e: PickerCanceledEvent): void => {
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_CANCELED, {
      fileId,
    });
    clearLastAuthorizedEmail();
    setIsPickerVisible(false);
  };

  const handleOauthResponse = async (e: OAuthResponseEvent): Promise<void> => {
    const tokenResponse = e.detail;
    const missingScopes = getScopesMissingFromTokenResponse(tokenResponse, [DRIVE_SCOPE, USERINFO_SCOPE]);
    const hasDriveScope = !missingScopes.includes(DRIVE_SCOPE);
    const hasAccessToken = Boolean(tokenResponse.access_token);

    // Sometimes the Picker component can call handleOauthResponse when the scopes aren't granted. In that case,
    // don't fetch and store authorized email, and set picker visibility to false. Note: setting picker visible to false
    // won't close any iframes that were opened up by the Picker component. The user will manually have to close those.
    if (tokenResponse.error || missingScopes.length > 0) {
      const hasTokenError = Boolean(tokenResponse.error);
      const hasMissingScopes = missingScopes.length > 0;
      let reason: string;
      if (hasTokenError && hasMissingScopes) {
        reason = 'missing_scopes_and_token_error';
      } else if (hasMissingScopes) {
        reason = 'missing_scopes';
      } else {
        reason = 'token_error';
      }

      if (!hasLoggedOauthFailure) {
        hasLoggedOauthFailure = true;
        Logging.warn('GDrive Picker OAuth failure — incomplete consent or token error', {
          extra: {
            reason,
            error: tokenResponse.error ?? null,
            grantedScopes: tokenResponse.scope ?? '',
            missingScopes,
            hasDriveScope,
            hasAccessToken,
          },
        });
        Metrics.count('notebook.gdrive_picker.oauth_failure', 1, {reason});
      }

      clearLastAuthorizedEmail();
      setIsPickerVisible(false);
      return;
    }

    await fetchAndStoreAuthorizedEmail(e.detail.access_token);
  };

  const handleOauthError = (e: OAuthErrorEvent): void => {
    if (!hasLoggedOauthFailure) {
      hasLoggedOauthFailure = true;
      Logging.warn('GDrive Picker OAuth error event', {
        extra: {
          type: e.type,
          detail: e.detail,
        },
      });
      Metrics.count('notebook.gdrive_picker.oauth_failure', 1, {reason: 'oauth_error_event'});
    }

    clearLastAuthorizedEmail();
    setIsPickerVisible(false);
  };

  // TODO(BNCH-152463): We are collecting all files in an array from the async generator validateAndDownloadGoogleDriveFiles, then passing
  // that array to onGoogleDriveFilesReady. This defeats the purpose of using an async generator to reduce the amount of File blobs stored in
  // memory at once.
  //
  // To fix this, refactor NotePartGrid's file upload logic so it can upload a file one-at-a-time rather than needing the entire array
  // of files. Then, change the usage of onGoogleDriveFilesReady here so we pass in each file one at a time to it, rather than collecting
  // them in an array first.

  const handlePicked = async (e: PickerPickedEvent): Promise<void> => {
    setIsPickerVisible(false);
    const docs = e.detail.docs ?? [];
    if (docs.length === 0) {
      clearLastAuthorizedEmail();
      return;
    }

    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_FILES_SELECTED, {
      fileId,
      file_count: docs.length,
    });

    let accessToken: string;
    try {
      accessToken = await getAccessToken({
        scopes: [DRIVE_SCOPE],
        loginHint: getLastAuthorizedEmail() ?? undefined,
      });
    } catch (err) {
      clearLastAuthorizedEmail();
      if (showGetAccessTokenErrorToast(err)) {
        return;
      }
      throw err;
    }

    // We've already consumed the last authorized email -- clear it now
    clearLastAuthorizedEmail();

    let downloadedFileCount = 0;
    const downloadToastId = ToastActions.pushToast({
      type: ToastType.INFO,
      message: getDownloadToastMessage(downloadedFileCount, docs.length),
      duration: 0,
    });

    try {
      const files: Array<File> = [];
      for await (const downloadResult of validateAndDownloadGoogleDriveFiles(docs, accessToken)) {
        const {file, error} = downloadResult;
        if (error != null) {
          ToastActions.pushToast({
            type: ToastType.DANGER,
            message: error.userDescription,
            duration: 0,
          });
        } else if (file != null) {
          files.push(file);
          downloadedFileCount += 1;
          ToastActions.pushToast({
            id: downloadToastId,
            type: ToastType.INFO,
            message: getDownloadToastMessage(downloadedFileCount, docs.length),
            duration: 0,
          });
        }
      }

      if (files.length > 0) {
        onGoogleDriveFilesReady(files);
      }
    } catch (err) {
      if (!(err instanceof GooglePickerAPIScriptNotLoadedError)) {
        throw err;
      }
      ToastActions.pushToast({
        type: ToastType.WARNING,
        message:
          'Google Drive integration not loaded. Please click on "Upload from Google Drive" and go through the authentication and file selection process again',
      });
    }

    // Leave a toast up for 5 seconds showing how many files were actually downloaded from Google Drive
    ToastActions.pushToast({
      id: downloadToastId,
      type: ToastType.INFO,
      message: getDownloadToastMessage(downloadedFileCount, docs.length),
      duration: 5,
    });
  };

  const resetOauthFailureFlag = (): void => {
    hasLoggedOauthFailure = false;
  };

  return {handleCanceled, handleOauthResponse, handleOauthError, handlePicked, resetOauthFailureFlag};
}
